<template>
  <div v-if="$apollo.loading && !showMoreLoading">
    <div class="w-full h-[400px] bg-gray-lightest animate-pulse" role="status">
      <span class="sr-only">Loading...</span>
    </div>

    <div class="container mb-16">
      <BreadcrumbsSkeleton class="my-10" />

      <TabsSkeleton class="my-10" />

      <ParagraphSkeleton class="my-10" />

      <div
        class="space-y-6 md:grid md:gap-16 lg:grid-cols-4 md:grid-cols-2 md:space-y-0"
      >
        <PostCardSkeleton
          v-for="n in 3"
          :key="n"
          :class="{ 'md:col-span-2': n === 1 }"
        />

        <ActionSkeleton class="col-span-full" />
      </div>
    </div>
  </div>
  <div v-else>
    <Visual
      :image="visualImage"
      image-bg-position-classes="bg-[right_-50rem_center] lg:bg-center"
      :title="title"
      :subtitle="visualSubtitle"
    />
    <div class="container mx-auto">
      <div class="flex my-10">
        <Breadcrumbs v-if="tag" :items="breadcrumbItems" />
      </div>

      <div
        class="flex flex-row my-10 overflow-x-scroll flex-nowrap whitespace-nowrap"
      >
        <div
          v-for="(cat, key) in featuredCategories"
          :key="key"
          class="flex flex-col flex-1 w-full border-b group border-b-gray-lightest"
        >
          <NuxtLink
            v-if="!cat.active"
            :key="key"
            :to="cat.url"
            class="w-full py-[8px] px-[20px] font-bold group-hover:text-blue-dark text-blue text-subtitle uppercase border-b-2 border-b-transparent group-hover:border-b-blue"
          >
            {{ cat.label }}
          </NuxtLink>
          <button
            v-else
            type="button"
            class="text-left w-full py-[8px] px-[20px] cursor-default font-bold text-blue-dark text-subtitle uppercase border-b-2 border-b-blue"
          >
            {{ cat.label }}
          </button>
        </div>
      </div>

      <div class="my-10">
        <p class="text-center">{{ description }}</p>
      </div>

      <div
        class="space-y-6 md:grid md:gap-16 lg:grid-cols-4 md:grid-cols-2 md:space-y-0"
      >
        <template
          v-for="({ node: post, cursor, showCategory }, n) in getFirstTreePosts"
        >
          <PostCard
            :key="cursor"
            :class="{ 'md:col-span-2': n === 0 }"
            :show-category="showCategory"
            :post="post"
            type="vertical"
            category-size="small"
          />
        </template>

        <Action v-bind="actionBanner" size="wide" class="col-span-full" />

        <template
          v-for="{ node: post, cursor, showCategory } in getPostsAfterTree"
        >
          <PostCard
            :key="cursor"
            :show-category="showCategory"
            :post="post"
            type="vertical"
            category-size="small"
          />
        </template>

        <template v-if="$apollo.queries.tag.loading">
          <PostCardSkeleton v-for="n in 8" :key="n" />
        </template>
      </div>

      <div v-if="posts" class="flex flex-col items-center w-full my-10">
        <p class="font-bold uppercase font-xl">
          {{ posts.edges.length }}/{{ posts.pageInfo.total }}
        </p>
        <button
          type="button"
          class="flex flex-row py-[13px] px-8 rounded font-bold text-subtitle items-center"
          :class="[
            {
              'bg-blue hover:bg-blue-dark text-white':
                posts.pageInfo.hasNextPage,
              'bg-gray-lightest text-gray-light cursor-default': !posts.pageInfo
                .hasNextPage,
            },
          ]"
          @click="showMore"
        >
          Mostra di più
        </button>
      </div>

      <Action
        title="Numero verde"
        content="Da telefono fisso e cellulare 800.89.69.68 (dall'estero +39 02 84251701), attivo dal lunedì al venerdì dalle 9.00 alle 18.00"
        class="my-9.6"
      >
        <template #icon>
          <SvgIcon name="phone" class="h-9.6 w-9.6" />
        </template>
      </Action>
    </div>
  </div>
</template>

<script>
// Components
import Action from '~/components/News/Action.vue'
import ActionSkeleton from '~/components/News/ActionSkeleton.vue'
import Breadcrumbs from '~/components/News/Breadcrumbs.vue'
import BreadcrumbsSkeleton from '~/components/News/BreadcrumbsSkeleton.vue'
import ParagraphSkeleton from '~/components/News/ParagraphSkeleton.vue'
import PostCard from '~/components/News/PostCard.vue'
import PostCardSkeleton from '~/components/News/PostCardSkeleton.vue'
import TabsSkeleton from '~/components/News/TabsSkeleton.vue'
import Visual from '~/components/News/Visual.vue'

// GraphQL
import settings from '~/apollo/queries/settings.gql'
import tag from '~/apollo/queries/tag.gql'

export default {
  components: {
    Action,
    ActionSkeleton,
    Breadcrumbs,
    BreadcrumbsSkeleton,
    ParagraphSkeleton,
    PostCard,
    PostCardSkeleton,
    TabsSkeleton,
    Visual,
  },
  apollo: {
    settings: {
      query: settings,
      prefetch: true,
      update(data) {
        return data
      },
    },
    tag: {
      query: tag,
      prefetch: ({ route }) => ({
        id: route.params.slug,
        categoryId: Number(route.query.catID),
      }),
      variables() {
        return {
          id: this.$route.params.slug,
          categoryId: Number(this.$route.query.catID),
        }
      },
      update(data) {
        if (!data?.tag) {
          this.$nuxt.error({ statusCode: 404, message: 'Page not found' })
        }
        return data?.tag
      },
    },
  },
  filters: {
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
      return new Date(date).toLocaleDateString('it-IT', options)
    },
  },
  data() {
    return {
      showMoreLoading: false,
    }
  },
  head() {
    return {
      title: this.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.tag?.seo?.metaDesc ?? this.description,
        },
        {
          hid: 'robots',
          name: 'robots',
          content: [
            this.tag?.seo?.metaRobotsNoindex ?? 'index',
            this.tag?.seo?.metaRobotsNofollow ?? 'follow',
          ].join(','),
        },
        {
          hid: 'og:type',
          name: 'og:type',
          content: 'object',
        },
        {
          hid: 'og:title',
          name: 'og:title',
          content: this.tag?.seo?.opengraphTitle,
          skip: !this.tag?.seo?.opengraphTitle,
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.tag?.seo?.opengraphDescription,
          skip: !this.tag?.seo?.opengraphDescription,
        },
        {
          hid: 'og:url',
          name: 'og:url',
          content: this.tag?.seo?.opengraphUrl,
          skip: !this.tag?.seo?.opengraphUrl,
        },
        {
          hid: 'og:site_name',
          name: 'og:site_name',
          content: this.tag?.seo?.opengraphSiteName,
          skip: !this.tag?.seo?.opengraphSiteName,
        },
      ],
      script: [
        {
          type: 'application/ld+json',
          json: this.schema,
        },
        {
          type: 'text/javascript',
          innerHTML: `var utag_data = ${JSON.stringify({
            site_name: this.settings?.generalSettings?.title,
            site_description: this.settings?.generalSettings?.description ?? '',
            page_type: 'tag-archive',
            post_title: `Tag archive: ${this.title}`,
            user_role: 'guest',
          })}`,
        },
      ],
      __dangerouslyDisableSanitizers: ['innerHTML'],
    }
  },
  computed: {
    options() {
      return this.settings?.acfOptions?.acfOptions
    },
    posts() {
      return this.tag?.posts
    },
    getFirstTreePosts() {
      return this.posts?.edges.slice(0, 3).map(({ node: post, cursor }) => ({
        cursor,
        showCategory: !this.$route.query.catID,
        node: this.$utilities.mapPost(post),
      }))
    },
    getPostsAfterTree() {
      return this.posts?.edges.slice(3).map(({ node: post, cursor }) => ({
        cursor,
        showCategory: !this.$route.query.catID,
        node: this.$utilities.mapPost(post),
      }))
    },
    computedPosts() {
      return this.posts?.edges.map(({ node: post, cursor }) => ({
        cursor,
        node: this.$utilities.mapPost(post),
      }))
    },
    featuredCategories() {
      return [
        {
          label: 'Tutte',
          url: `/news/tag/${this.$route.params.slug}/`,
          active: !this.$route.query.catID,
        },
        ...this.options?.categorieInEvidenza.map(({ categoria }) => ({
          label: categoria.name,
          url: `/news/tag/${this.$route.params.slug}/?catID=${categoria.databaseId}`,
          active: this.$route.query.catID === categoria.databaseId.toString(),
        })),
      ]
    },
    title() {
      return this.tag?.name
    },
    visualSubtitle() {
      return this.tag?.acfCategory?.sottotitolo
    },
    visualImage() {
      if (!this.tag?.acfCategory?.immagine.mediaItemUrl) {
        return
      }
      const image = this.$utilities.imgAlias(
        this.tag?.acfCategory?.immagine.mediaItemUrl
      )
      return this.$img(image, { height: 450 })
    },
    breadcrumbItems() {
      return this.tag?.seo.breadcrumbs.map((item) => ({
        text: item.text,
        url: new URL(item.url).pathname,
      }))
    },
    seoTitle() {
      return this.tag?.seo.title
    },
    description() {
      return this.tag?.description
    },
    schema() {
      return this.tag && JSON.parse(this.tag.seo.schema.raw)
    },
    actionBanner() {
      return {
        title: this.options?.disclaimerArticoli.messaggio,
        showButton: true,
        actionText: this.options?.disclaimerArticoli.linkBottone.title,
        handleAction: () => {
          this.$router.push(this.options?.disclaimerArticoli.linkBottone.url)
        },
      }
    },
  },
  methods: {
    showMore() {
      this.showMoreLoading = true
      this.$apollo.queries.tag.fetchMore({
        variables: {
          id: this.$route.params.slug,
          first: 8,
          after: this.tag.posts.pageInfo.endCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          this.showMoreLoading = false
          const posts = fetchMoreResult.tag?.posts.edges
          const pageInfo = fetchMoreResult.tag?.posts.pageInfo
          return {
            ...previousResult,
            tag: {
              ...previousResult.tag,
              posts: {
                __typename: previousResult.tag.posts.__typename,
                // Merging the tag list
                edges: [...previousResult.tag.posts.edges, ...posts],
                pageInfo: {
                  ...previousResult.tag.posts.pageInfo,
                  ...{
                    endCursor: pageInfo.endCursor,
                    hasNextPage: pageInfo.hasNextPage,
                    hasPreviousPage: pageInfo.hasPreviousPage,
                  },
                },
              },
            },
          }
        },
      })
    },
  },
}
</script>
